import React, { useState } from 'react';
/**
 * LiveRulebookCard — Renders a single ModuleRulebook from the live
 * RULEBOOKS_REGISTRY (governance/rulebooks). Phase 3 Strak-Trek.
 *
 * Replaces the static markdown-metadata RulebookCard for module rulebooks.
 * Every section here is sourced from executable code — what you see is what
 * the pipeline actually enforces.
 *
 * Also surfaces a "Toekomstig (roadmap)" panel sourced from
 * src/governance/rulebooks/roadmaps.ts — aspirational future question types
 * and visuals that aren't enforced yet but signal the design trajectory.
 */
import { ChevronDownIcon, ChevronRightIcon, BookOpenIcon, CheckCircleIcon, TypeIcon, AlertTriangleIcon, PaletteIcon, LayersIcon, SparklesIcon } from 'lucide-react';
import type { ModuleRulebook } from '../../../../governance';
import { getModuleRoadmap } from '../../../../governance';
import { RULEBOOK_COLOR_MAP } from './governanceRulesData';
type Props = {
  rulebook: ModuleRulebook;
  color: string;
};
export function LiveRulebookCard({
  rulebook,
  color
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const c = RULEBOOK_COLOR_MAP[color] ?? RULEBOOK_COLOR_MAP.blue;
  const notationFlags: string[] = [];
  if (rulebook.notation.requireLatexFractions) notationFlags.push('LaTeX-breuken');
  if (rulebook.notation.requireCommaDecimals) notationFlags.push('Komma-decimalen');
  if (rulebook.notation.requireSuperscriptUnits) notationFlags.push('Superscript-eenheden (m², m³)');
  if (rulebook.notation.requireEuroComma) notationFlags.push('Euro-komma');
  const roadmap = getModuleRoadmap(rulebook.moduleId);
  return <div className={`rounded-lg border ${c.border} bg-white overflow-hidden`} data-id="element-4796" data-mp="1tjpcg0cy4ikw">
      <button className="flex w-full items-start gap-3 p-4 text-left hover:bg-gray-50 transition-colors" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} data-id="element-4797" data-mp="1xk87026842qs">
        
        <BookOpenIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${c.badge.split(' ')[1]}`} data-id="element-4798" data-mp="1jn2tjh46k1li" />
        
        <div className="min-w-0 flex-1" data-id="element-4799" data-mp="1olntdffisj9f">
          <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-4800" data-mp="3q98yasio0ohm">
            <span className="font-semibold text-gray-800" data-id="element-4801" data-mp="3liae3huk3693">
              {rulebook.displayName}
            </span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${c.badge}`} data-id="element-4802" data-mp="3hg5kgj43y9mg">
              
              {rulebook.shortCode}
            </span>
            <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-100" data-id="element-4803" data-mp="3cfhk36tzogfk">
              live
            </span>
            {roadmap && (roadmap.futureQuestionTypes.length > 0 || roadmap.futureVisuals.length > 0) && <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-100 flex items-center gap-1" data-id="element-4804" data-mp="40podo6h75tf">
                  <SparklesIcon className="h-3 w-3" data-id="element-4805" data-mp="2orq9c7o3kbge" />
                  {roadmap.futureQuestionTypes.length + roadmap.futureVisuals.length}{' '}
                  toekomstig
                </span>}
          </div>
          <p className="text-sm text-gray-600 mt-1" data-id="element-4806" data-mp="29pi3iptj9ib1">
            {rulebook.didactics.coreSummary}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-4807" data-mp="2en9vfju6h4f8">
            {rulebook.didactics.principles.slice(0, 4).map(p => <span key={p} className="rounded px-1.5 py-0.5 text-[10px] font-mono bg-gray-50 text-gray-500 border border-gray-100" data-id="element-4808" data-mp="2jm4l95qkbt8u">
              
                {p}
              </span>)}
          </div>
        </div>
        <div className="flex-shrink-0 text-gray-400" data-id="element-4809" data-mp="2sey41d57xarg">
          {expanded ? <ChevronDownIcon className="h-4 w-4" data-id="element-4810" data-mp="2xdrjvc7z9dus" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-4811" data-mp="2xcpwc89ryyw6" />}
        </div>
      </button>

      {expanded && <div className={`border-t ${c.border} ${c.bg} px-4 py-3 space-y-4`} data-id="element-4812" data-mp="32e6u3uwyq58j">
          {/* Key rules */}
          <Section icon={<CheckCircleIcon className="h-4 w-4 text-gray-400" data-id="element-4814" data-mp="37ansdhxvlzzc" />} title="Kernregels" data-id="element-4813" data-mp="37byxpm6yzfsk">
          
            <ul className="space-y-1.5" data-id="element-4815" data-mp="181pglf1xejh3">
              {rulebook.keyRules.map((rule, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-4816" data-mp="1cz92n14v80jl" data-mpi={i}>
              
                  <CheckCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-400" data-id="element-4817" data-mp="33e9pdqtywhjw" />
                  {rule}
                </li>)}
            </ul>
          </Section>

          {/* Didactics */}
          <Section icon={<LayersIcon className="h-4 w-4 text-gray-400" data-id="element-4819" data-mp="3ceevlxnfd9wh" />} title="Didactische context" data-id="element-4818" data-mp="3cfzid13hzojs">
          
            <div className="space-y-1.5 text-sm" data-id="element-4820" data-mp="37fbo86jz2rlc">
              <DetailRow label="Schoolboek-methodes" value={rulebook.didactics.schoolbookReferences.join(', ')} data-id="element-4821" data-mp="2jr26pgtskq00" />
            
              <DetailRow label="Misconceptie-domein" value={<code className="font-mono text-xs" data-id="element-4823" data-mp="2thdejegljjc6">
                    {rulebook.didactics.misconceptionDomain}
                  </code>} data-id="element-4822" data-mp="2frthmp9p5gas" />
            
            </div>
          </Section>

          {/* Notation */}
          {(notationFlags.length > 0 || (rulebook.notation.forbiddenPatterns?.length ?? 0) > 0) && <Section icon={<TypeIcon className="h-4 w-4 text-gray-400" data-id="element-4825" data-mp="1w314sqr4c7tk" />} title="Notatie" data-id="element-4824" data-mp="1w2st326qf8jy">
          
              {notationFlags.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2" data-id="element-4826" data-mp="bjfa2c5xs5q7">
                  {notationFlags.map(flag => <span key={flag} className="rounded-full px-2 py-0.5 text-xs bg-white border border-gray-200 text-gray-700" data-id="element-4827" data-mp="gbyw7xiow96m">
              
                      {flag}
                    </span>)}
                </div>}
              {rulebook.notation.forbiddenPatterns?.map((p, i) => <p key={i} className="text-xs text-red-600" data-id="element-4828" data-mp="v8hd9zdh1ndn">
                  <code className="font-mono bg-red-50 px-1.5 py-0.5 rounded mr-2" data-id="element-4829" data-mp="v63hfoayw751">
                    {p.pattern}
                  </code>
                  {p.label}
                </p>)}
            </Section>}

          {/* Visuals */}
          {(rulebook.visuals.preferredAidTypes?.length ?? 0) > 0 && <Section icon={<PaletteIcon className="h-4 w-4 text-gray-400" data-id="element-4831" data-mp="qaxx2tx7r0j2" />} title="Voorkeurs-visuals" data-id="element-4830" data-mp="q7a3xvvokg4v">
          
              <div className="flex flex-wrap gap-1.5" data-id="element-4832" data-mp="362lsum0hqn0a">
                {rulebook.visuals.preferredAidTypes?.map(v => <span key={v} className="rounded px-1.5 py-0.5 text-xs font-mono bg-white border border-gray-200 text-gray-700" data-id="element-4833" data-mp="3awzi06ok7xwi">
              
                    {v}
                  </span>)}
              </div>
              {rulebook.visuals.familyVisualHints && rulebook.visuals.familyVisualHints.length > 0 && <div className="mt-2 space-y-1" data-id="element-4834" data-mp="2i2kxl76hr5tt">
                    {rulebook.visuals.familyVisualHints.map((h, i) => <p key={i} className="text-xs text-gray-600" data-id="element-4835" data-mp="286q6dno7c2ff" data-mpi={i}>
                        <code className="font-mono text-[10px] bg-white px-1 py-0.5 rounded mr-1.5" data-id="element-4836" data-mp="28819rmdqj860">
                          {h.familyIdPattern}
                        </code>
                        {h.note}
                      </p>)}
                  </div>}
            </Section>}

          {/* Question types */}
          {rulebook.questionTypes.preferredTypes.length > 0 && <Section icon={<TypeIcon className="h-4 w-4 text-gray-400" data-id="element-4838" data-mp="3tirn1cx03faz" />} title="Voorkeurs-vraagtypes" data-id="element-4837" data-mp="3tkcid4r5329w">
          
              <div className="flex flex-wrap gap-1.5" data-id="element-4839" data-mp="3omaxqfe2euwb">
                {rulebook.questionTypes.preferredTypes.map(t => <span key={t} className="rounded px-1.5 py-0.5 text-xs font-mono bg-white border border-gray-200 text-gray-700" data-id="element-4840" data-mp="3w3pj2lg06ld2">
              
                    {t}
                  </span>)}
              </div>
              {rulebook.questionTypes.customTypeRoles && rulebook.questionTypes.customTypeRoles.length > 0 && <div className="mt-2 space-y-1" data-id="element-4841" data-mp="en7qvijr3lma">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold" data-id="element-4842" data-mp="3d66a1htjp2qn">
                      Custom-rollen
                    </p>
                    {rulebook.questionTypes.customTypeRoles.map(r => <div key={r.type} className="text-xs text-gray-600 flex items-center gap-2" data-id="element-4843" data-mp="3n3kppf7n9nhw">
              
                        <code className="font-mono" data-id="element-4844" data-mp="3r6h56x1w13hm">{r.type}</code>
                        <span className="text-gray-400" data-id="element-4845" data-mp="12dvah1313zoo">·</span>
                        <span data-id="element-4846" data-mp="12foa3wf85jn6">{r.didacticRole}</span>
                        <span className="text-gray-400" data-id="element-4847" data-mp="16ef8s7zc99ce">·</span>
                        <span data-id="element-4848" data-mp="1sgqkejftxnmo">DOK {r.preferredDok}</span>
                      </div>)}
                  </div>}
            </Section>}

          {/* Distractor traps */}
          {rulebook.distractorTraps.length > 0 && <Section icon={<AlertTriangleIcon className="h-4 w-4 text-gray-400" data-id="element-4850" data-mp="226a7606ny1mt" />} title="Trap-distractors (val-afleiders)" data-id="element-4849" data-mp="226jv7i85dypv">
          
              <ul className="space-y-1.5" data-id="element-4851" data-mp="1xesfy3ufzff1">
                {rulebook.distractorTraps.map(trap => <li key={trap.id} className="text-sm" data-id="element-4852" data-mp="2b6p2lsdccx41">
                    <p className="font-medium text-gray-800" data-id="element-4853" data-mp="2b9bbbvbyxinu">{trap.label}</p>
                    <p className="text-xs text-gray-600" data-id="element-4854" data-mp="267lxy919wswl">{trap.description}</p>
                  </li>)}
              </ul>
            </Section>}

          {/* Toekomstig (roadmap) — aspirational, not enforced */}
          {roadmap && (roadmap.futureQuestionTypes.length > 0 || roadmap.futureVisuals.length > 0) && <div className="rounded-lg border border-dashed border-violet-200 bg-violet-50/40 p-3" data-id="element-4855" data-mp="qrww5vx0dgo7">
                <p className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-2 flex items-center gap-1.5" data-id="element-4856" data-mp="ltv2yi5vc0z0">
                  <SparklesIcon className="h-4 w-4" data-id="element-4857" data-mp="2xhzx1zj9dk92" />
                  Toekomstig — roadmap (niet afgedwongen)
                </p>
                <p className="text-[11px] text-violet-700/80 mb-3 leading-relaxed" data-id="element-4858" data-mp="2nqcdeksy17i5">
                  Aspirationele vraagtypes en visuals die we voor deze module
                  willen bouwen. Geen pipeline-check, wel ontwerprichting.
                </p>

                {roadmap.futureQuestionTypes.length > 0 && <div className="mb-3" data-id="element-4859" data-mp="2ir96svxbet82">
                    <p className="text-[10px] uppercase tracking-wide text-violet-600 font-semibold mb-1.5 flex items-center gap-1" data-id="element-4860" data-mp="2is2fi2fu3ssu">
                      <TypeIcon className="h-3 w-3" data-id="element-4861" data-mp="29q1klc82piuc" />
                      Vraagtypes ({roadmap.futureQuestionTypes.length})
                    </p>
                    <ul className="space-y-1.5" data-id="element-4862" data-mp="30wnds1jtmtk">
                      {roadmap.futureQuestionTypes.map(t => <li key={t.id} className="text-xs text-gray-700 bg-white/60 rounded px-2 py-1.5 border border-violet-100" data-id="element-4863" data-mp="7xn5sm0tsui5">
                
                          <div className="flex flex-wrap items-center gap-1.5" data-id="element-4864" data-mp="1jq5wetxw9n37">
                            <span className="font-medium text-gray-800" data-id="element-4865" data-mp="1ok0fbv5ody18">
                              {t.label}
                            </span>
                            <code className="font-mono text-[10px] text-violet-600 bg-violet-50 px-1 rounded" data-id="element-4866" data-mp="1fpvubsd83gtm">
                              {t.id}
                            </code>
                            {t.tierWindow && <span className="text-[10px] text-gray-500" data-id="element-4867" data-mp="27i390n964j5d">
                                tier {t.tierWindow.min}–{t.tierWindow.max}
                              </span>}
                          </div>
                          <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-4868" data-mp="1yfu1m69755w7">
                            {t.rationale}
                          </p>
                          {t.targetFamilyPatterns && t.targetFamilyPatterns.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-4869" data-mp="n2j51afoguwm">
                                {t.targetFamilyPatterns.map(p => <code key={p} className="font-mono text-[9px] text-gray-500 bg-gray-50 px-1 rounded" data-id="element-4870" data-mp="fgnqfo7r6ssi">
                    
                                    {p}
                                  </code>)}
                              </div>}
                        </li>)}
                    </ul>
                  </div>}

                {roadmap.futureVisuals.length > 0 && <div data-id="element-4871" data-mp="3ryubwp90by5a">
                    <p className="text-[10px] uppercase tracking-wide text-violet-600 font-semibold mb-1.5 flex items-center gap-1" data-id="element-4872" data-mp="3s0f2y4w44yye">
                      <PaletteIcon className="h-3 w-3" data-id="element-4873" data-mp="3nw7dg5htzez4" />
                      Visuals ({roadmap.futureVisuals.length})
                    </p>
                    <ul className="space-y-1.5" data-id="element-4874" data-mp="t2klmjhufgw">
                      {roadmap.futureVisuals.map(v => <li key={v.id} className="text-xs text-gray-700 bg-white/60 rounded px-2 py-1.5 border border-violet-100" data-id="element-4875" data-mp="398mcoi8fhrud">
                
                          <div className="flex flex-wrap items-center gap-1.5" data-id="element-4876" data-mp="349rjqngqymwj">
                            <span className="font-medium text-gray-800" data-id="element-4877" data-mp="1863m2odcn6ob">
                              {v.label}
                            </span>
                            <code className="font-mono text-[10px] text-violet-600 bg-violet-50 px-1 rounded" data-id="element-4878" data-mp="1hwpwm467l705">
                              {v.id}
                            </code>
                            <span className={`text-[9px] uppercase tracking-wide px-1 rounded ${v.usage === 'stimulus' ? 'bg-blue-50 text-blue-700' : v.usage === 'support' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-4879" data-mp="1mxvojzs1oq8m">
                    
                              {v.usage}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-4880" data-mp="138la0p41cbc3">
                            {v.rationale}
                          </p>
                          {v.targetFamilyPatterns && v.targetFamilyPatterns.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-4881" data-mp="2ele0lwl3atqk">
                                {v.targetFamilyPatterns.map(p => <code key={p} className="font-mono text-[9px] text-gray-500 bg-gray-50 px-1 rounded" data-id="element-4882" data-mp="2ytybx2mqfao">
                    
                                    {p}
                                  </code>)}
                              </div>}
                        </li>)}
                    </ul>
                  </div>}
              </div>}
        </div>}
    </div>;
}
function Section({
  icon,
  title,
  children
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-4883" data-mp="3be41l8i99qk1">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5" data-id="element-4884" data-mp="3bhrtk381y4wn">
        {icon}
        {title}
      </p>
      {children}
    </div>;
}
function DetailRow({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) {
  return <div className="flex items-start gap-2 text-sm" data-id="element-4885" data-mp="15lp60r0fsexm">
      <span className="text-gray-500 min-w-[140px]" data-id="element-4886" data-mp="10muf11sbfk2f">{label}:</span>
      <span className="text-gray-700" data-id="element-4887" data-mp="10lja2yn4w69q">{value}</span>
    </div>;
}